import React, { useMemo, useEffect, useState } from "react";

import { useTranslation } from "react-i18next";

import useActionBooking from "../../../../../../../components/Booking/actions";
import { useBookingFiles } from "../../../../../../../components/Booking/hooks/useBookingFiles";
import { useDocuments } from "../../../../../../../components/Booking/hooks/useDocuments";
import {
  calculateBookingCartonTotals,
  calculatePurchaseOrderTotal,
  calculateScannedItemsFromBooking,
} from "../../../../../../../components/Booking/Maintenance/util";
import { BookingTabs } from "../../../../../../../components/Booking/util";
import CartonSSCC from "../../../../../../../components/Generic/CartonSSCC";
import { CartonSSCCFrom } from "../../../../../../../components/Generic/CartonSSCC/util";
import ShipmentOrders from "../../../../../../../components/Generic/ShipmentOrders";
import { PurchaserOrderFrom } from "../../../../../../../components/Generic/ShipmentOrders/types";
import SideView from "../../../../../../../components/Generic/SideView";
import DefaultViewSkeleton from "../../../../../../../components/Generic/SideView/Skeleton";
import { useShipmentOrdersRecourse } from "../../../../../../../components/Shipments/context/AvailableOrders";
import { useShipmentResourcesContext } from "../../../../../../../components/Shipments/hooks/useResourceContext";
import { useShipmentContext } from "../../../../../../../components/Shipments/hooks/useShipmentContext";
import Documents from "../../../../../../../components/Shipments/Maintenance/Documents";
import { ShipmentKey } from "../../../../../../../components/Shipments/types";
import { VoyageDetails } from "Components/Tracking";
import { useAppState } from "../../../../../../../context/StateContext";
import useActionShipment from "../../../../../../../hooks/useActionShipment";
import { ContainerPosition, ContainerStatus } from "../../../../../../../models/marine.traffic";
import { CountryCode, CountryName } from "../../../../../../../util/enums";
import { ShipmentViewTab } from "../../types";
import { ShipmentViewDetails } from "../Details";

interface ShipmentViewHandles {
  keyId: number | undefined;
  onCloseSidebar: () => void;
  isSidebarOpened: boolean;
  defaultTab?: string;
}

export const ShipmentViewTabs = ({
  keyId,
  isSidebarOpened,
  onCloseSidebar,
  defaultTab,
}: ShipmentViewHandles) => {
  const { getOrdersByBooking, getCartonsByBooking } = useActionBooking();
  const { t: translate } = useTranslation();
  const { shipment, setShipment, handleUpdateShipment } = useShipmentContext();
  const [hasDocumentsChanged, setHasDocumentsChanged] = useState<boolean>(false);
  const [containerPosition, setContainerPosition] = useState<ContainerPosition>();
  const [containerStatus, setContainerStatus] = useState<ContainerStatus>();

  const { isFMorSPVersion } = useAppState();
  const resourceOrders = useMemo(() => shipment?.purchaseOrders || [], [shipment?.purchaseOrders]);
  const {
    onDownloadDocument,
    onDownloadDocumentCsv,
    loadingCartonLogs,
    loadingBookingDocuments,
    loadingCiplCsv,
    loadingAllDocumentsZip,
    onDownloadAllDocuments,
    loadingSavingCsvDocument,
    saveShipmentDocuments,
    loadingUpdateShipmentDocuments,
  } = useBookingFiles({
    bookingId: shipment?.bookingId || shipment?.id,
    hasBookingId: !!shipment?.bookingId,
    documents: shipment?.documents,
  });

  const { getShipmentById, getMarineStatus } = useActionShipment();
  const { orderList, refetchOrderList, isLoading: loadindOrderList } = useShipmentOrdersRecourse();

  const { portList, companyDocuments, loading: loadingResources } = useShipmentResourcesContext();
  const { documentsJoinned } = useDocuments({ documents: shipment?.documents });
  const { data: shipmentData, isInitialLoading: loadingShipment } = getShipmentById(Number(keyId));
  const { data: ordersShipmentData, isInitialLoading: loadingOrders } = getOrdersByBooking(
    shipment?.bookingId || Number(keyId)
  );
  const { data: cartonsShipmentData, isInitialLoading: loadingCartons } = getCartonsByBooking(
    shipment?.bookingId || Number(keyId)
  );
  const { isFetching: isLoadingMarineStatus } = getMarineStatus(
    setContainerStatus,
    setContainerPosition
  );

  const isDischargePortFromBrazil = useMemo(() => {
    let countryCode;

    if (isFMorSPVersion) countryCode = shipment?.portOfDischarge?.country?.code;
    else
      countryCode =
        portList.find((port) => port.id === shipment?.portOfDischargeId)?.country?.code ??
        "countryCode";

    return countryCode === CountryCode.BRAZIL;
  }, [shipment?.portOfDischargeId, portList]);

  const ORIGIN_CRITERION_ORIGIN_COUNTRIES = [
    CountryCode.BANGLADESH,
    CountryCode.INDIA,
    CountryCode.CAMBODIA,
    CountryCode.INDONESIA,
    CountryCode.PAKISTAN,
  ];

  const showOriginCriterion = useMemo(() => {
    const hasQualifyingOrigin = shipment?.purchaseOrders?.some((po) => {
      const country = po.supplier?.location?.country;
      return (
        !!country &&
        (ORIGIN_CRITERION_ORIGIN_COUNTRIES.includes(country as CountryCode) ||
          Object.values(CountryName).includes(country as CountryName))
      );
    });

    const destinationCode =
      shipment?.portOfDestination?.country?.code ??
      shipment?.portOfDischarge?.country?.code ??
      portList.find((port) => port.id === shipment?.portOfDestinationId)?.country?.code ??
      portList.find((port) => port.id === shipment?.portOfDischargeId)?.country?.code;

    return !!hasQualifyingOrigin && destinationCode === CountryCode.UNITED_KINGDOM;
  }, [shipment?.purchaseOrders, shipment?.portOfDestinationId, shipment?.portOfDestination, shipment?.portOfDischargeId, shipment?.portOfDischarge, portList]);

  const onUpdateBooking = (attribute: ShipmentKey, value: any) => {
    handleUpdateShipment(attribute, value);
  };

  useEffect(() => {
    if (shipmentData?.data) {
      setShipment((prev) => ({
        ...prev,
        ...shipmentData.data,
      }));
    }
  }, [shipmentData?.data]);

  useEffect(() => {
    if (cartonsShipmentData) {
      setShipment((prev) => ({
        ...prev,
        ...calculateBookingCartonTotals(cartonsShipmentData),
      }));
    }
  }, [cartonsShipmentData]);

  useEffect(() => {
    if (ordersShipmentData && cartonsShipmentData) {
      setShipment((prev) => ({
        ...prev,
        purchaseOrders: calculatePurchaseOrderTotal(ordersShipmentData, cartonsShipmentData),
      }));
    }
  }, [ordersShipmentData, cartonsShipmentData]);

  const purchaseOrders = useMemo(() => {
    let orders: string = "";

    if (shipment?.purchaseOrders && shipment?.purchaseOrders.length > 0) {
      const purchaseOrders = shipment?.purchaseOrders;

      for (const purchaseOrder of purchaseOrders) {
        orders += purchaseOrder?.reference ? `${purchaseOrder?.reference} - ` : "";
      }
      orders = orders.slice(0, -2);

      return orders;
    }

    return "";
  }, [shipment?.purchaseOrders]);

  const sidebarTitle = useMemo(() => {
    let title = translate("shipment");
    const reference = shipment?.containerId;
    const consigneeName = shipment?.consignee?.fullName || undefined;

    if (reference) title = `${title} #${reference}${consigneeName ? ` - ${consigneeName}` : ""}`;

    return title;
  }, [shipment]);

  useEffect(() => {
    if (hasDocumentsChanged) {
      saveShipmentDocuments();
    }
  }, [shipment?.documents, hasDocumentsChanged]);

  const tabs = useMemo(() => {
    const tabs: Array<ShipmentViewTab> = [
      {
        key: BookingTabs.DETAILS,
        label: translate(BookingTabs.DETAILS),
        loading: loadingShipment,
        isEmpty: !shipment,
        isOpened: false,
        render: () => <ShipmentViewDetails shipment={shipment} purchaseOrders={purchaseOrders} />,
        renderLoading: () => <DefaultViewSkeleton />,
      },
      {
        key: BookingTabs.ORDERS,
        label: translate(BookingTabs.ORDERS),
        loading: loadingOrders,
        isEmpty: !shipment?.purchaseOrders,
        isOpened: false,
        render: () => (
          <ShipmentOrders
            loadingOrderList={loadindOrderList}
            orderList={orderList}
            purchaseOrders={shipment?.purchaseOrders || []}
            refetchOrderList={refetchOrderList}
            viewOnly={{
              fields: true,
              actions: true,
            }}
            calledFrom={PurchaserOrderFrom.SHIPMENT}
            loadingOrders={loadingOrders}
            showOrderStatus={!shipment?.isBooking && !shipment?.bookingId}
          />
        ),
        renderLoading: () => <DefaultViewSkeleton />,
      },
      {
        key: BookingTabs.SSCC,
        label: translate(BookingTabs.SSCC),
        loading: loadingCartons || loadingResources,
        isEmpty: !shipment?.cartons,
        isOpened: false,
        render: () => (
          <CartonSSCC
            cartons={shipment?.cartons || []}
            loadingCartons={false}
            viewOnly={{
              fields: true,
              actions: true,
            }}
            viewSupplier={true}
            calledFrom={CartonSSCCFrom.BOOKING}
            subtitle={{
              quantity: calculateScannedItemsFromBooking(shipment),
              text: translate("scannedItems"),
            }}
            booking={shipment}
            resources={{
              purchaseOrders: resourceOrders,
            }}
            isLengthMandatory={isDischargePortFromBrazil}
            showOriginCriterion={showOriginCriterion}
            statusBooking={shipment?.statusShipment}
          />
        ),
        renderLoading: () => <DefaultViewSkeleton />,
      },
      {
        key: BookingTabs.DOCUMENTS,
        label: translate(BookingTabs.DOCUMENTS),
        loading: loadingShipment,
        isEmpty: !documentsJoinned,
        isOpened: false,
        render: () => (
          <Documents
            companyDocuments={companyDocuments}
            loadingResources={loadingResources}
            shipmentDocuments={documentsJoinned}
            openedFromView
            viewOnly={false}
            loadingShipment={loadingShipment}
            onDownloadDocument={shipment?.bookingId ? onDownloadDocument : undefined}
            onDownloadDocumentCsv={onDownloadDocumentCsv}
            loadingAllDocuments={
              loadingBookingDocuments ||
              loadingCiplCsv ||
              loadingCartonLogs ||
              loadingAllDocumentsZip ||
              loadingUpdateShipmentDocuments
            }
            onDownloadAllDocuments={onDownloadAllDocuments}
            onUpdateShipment={(attribute, value) => {
              onUpdateBooking(attribute, value);
              setHasDocumentsChanged(true);
              // handleChangeOptions(BookingObjects.DOCUMENTS);
            }}
            loadingUploadCsvDocument={loadingSavingCsvDocument}
          />
        ),
        renderLoading: () => <DefaultViewSkeleton />,
      },
      {
        key: BookingTabs.TRACKING,
        loading: loadingShipment || isLoadingMarineStatus,
        isEmpty: !containerStatus,
        label: translate(BookingTabs.TRACKING),
        render: () => (
          <VoyageDetails
            shipment={shipment}
            containerPosition={containerPosition}
            containerStatus={containerStatus}
            isOnViewComponent
            viewLivePosition={process.env.NODE_DATABASE !== "work"}
          />
        ),
        renderLoading: () => <DefaultViewSkeleton />,
      },
    ];

    return tabs;
  }, [
    shipmentData,
    loadingShipment,
    ordersShipmentData,
    loadingOrders,
    loadindOrderList,
    loadingResources,
    cartonsShipmentData,
    loadingCartons,
    shipment?.cartons,
    shipment,
    documentsJoinned,
    containerStatus,
    containerPosition,
    isLoadingMarineStatus,
    showOriginCriterion,
  ]);

  return (
    <SideView
      isLoading={loadingShipment}
      isOpened={isSidebarOpened}
      onClose={onCloseSidebar}
      tabs={tabs}
      title={sidebarTitle}
      buttonsComponent={[]}
      defaultTab={defaultTab ?? BookingTabs.DETAILS}
    />
  );
};
